import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAppContext } from '../context/AppContext';
import { fetchEventsFeed } from '../services';
import { WelcomeModal } from '../components/Modals';
import EventDrawer from '../components/EventDrawer';
import { getDummyEvents } from '../data/dummyDashboardData';
import {
  EASTERN_TZ,
  formatEasternDate,
  isInEasternMonth,
  isWithinEasternDateRange,
  normalizeEventsFeed,
} from '../utils/eventDates';
import { isTestEnvironment } from '../utils/isTestEnvironment';

const TIME_SAVINGS_MINUTES_BY_EVENT_TYPE = {
  'Invoice Created': 15,
  'Journal Entry Created': 30,
};

function getEventTimeSavingsMinutes(event) {
  return TIME_SAVINGS_MINUTES_BY_EVENT_TYPE[event.event_type] ?? 0;
}

const DATE_RANGE_OPTIONS = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '1000', label: 'All time' },
];

function isSuccessCode(code) {
  return code >= 200 && code < 300;
}

function formatAmount(amount) {
  return parseFloat(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatCurrency(amount) {
  return `$${formatAmount(amount)}`;
}

function formatTimeSavings(totalMinutes) {
  if (totalMinutes < 60) return `${totalMinutes} min`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (minutes === 0) return `${hours} hr${hours === 1 ? '' : 's'}`;
  return `${hours}h ${minutes}m`;
}

function parseEventAmount(amount) {
  const n = parseFloat(amount);
  return Number.isFinite(n) ? n : 0;
}

function isInvoiceEvent(event) {
  return event.target_type === 'Invoice'
    || (event.event_type != null && /invoice/i.test(event.event_type));
}

function getEventCustomer(event) {
  return event.target_customer ?? event.stream_data?.company_data?.name ?? '—';
}

function getEventRouteLabel(event) {
  if (event.source_system && event.target_system) {
    return `${event.source_system} → ${event.target_system}`;
  }
  return event.stream_data?.deal_data?.dealname ?? '—';
}

function getEventDocDisplay(event) {
  return event.target_doc_num ?? event.stream_data?.inv_data?.hs_number ?? null;
}

export const DashboardPage = () => {
  const { token, base_url, company, user } = useAppContext();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [eventTypeFilter, setEventTypeFilter] = useState('all');
  const [dateRangePreset, setDateRangePreset] = useState('30');
  const [selectedEvent, setSelectedEvent] = useState(null);

  const loadEvents = useCallback(async ({ background = false } = {}) => {
    if (!token || !base_url) return;
    if (!background) setLoading(true);
    try {
      const data = await fetchEventsFeed(token, base_url);
      const apiEvents = data.events || [];
      const eventsToShow = (apiEvents.length === 0 && isTestEnvironment())
        ? getDummyEvents()
        : apiEvents;
      setEvents(normalizeEventsFeed(eventsToShow));
    } catch (error) {
      console.error('Error loading events:', error);
      if (isTestEnvironment()) {
        setEvents(normalizeEventsFeed(getDummyEvents()));
      }
    } finally {
      if (!background) setLoading(false);
    }
  }, [token, base_url]);

  useEffect(() => {
    loadEvents();
    const intervalId = setInterval(() => loadEvents({ background: true }), 30_000);
    return () => clearInterval(intervalId);
  }, [loadEvents]);

  const firstName = user?.fname || user?.first_name || 'there';
  const companyName = company?.company_name || company?.name || 'Your company';

  const dateRangedEvents = useMemo(
    () => events.filter(e => isWithinEasternDateRange(e.create_date, dateRangePreset)),
    [events, dateRangePreset],
  );

  const { total, successCount, errorCount, successRate, monthInvoiceTotal, monthTimeSavingsMinutes } = useMemo(() => {
    const t = dateRangedEvents.length;
    const ok = dateRangedEvents.filter(e => isSuccessCode(e.status_code)).length;
    const err = t - ok;
    const rate = t ? Math.round((ok / t) * 100) : 0;
    const monthEvents = events.filter(e => isInEasternMonth(e.create_date));
    const invoiceTotal = monthEvents.reduce((sum, e) => {
      if (!isInvoiceEvent(e) || !isSuccessCode(e.status_code)) return sum;
      return sum + parseEventAmount(e.total_amount);
    }, 0);
    const timeSavingsMinutes = monthEvents.reduce(
      (sum, e) => sum + getEventTimeSavingsMinutes(e),
      0,
    );
    return {
      total: t,
      successCount: ok,
      errorCount: err,
      successRate: rate,
      monthInvoiceTotal: invoiceTotal,
      monthTimeSavingsMinutes: timeSavingsMinutes,
    };
  }, [dateRangedEvents, events]);

  const currentMonthLabel = useMemo(
    () => new Date().toLocaleString('en-US', { month: 'long', timeZone: EASTERN_TZ }),
    [],
  );

  const eventTypes = useMemo(
    () => ['all', ...Array.from(new Set(dateRangedEvents.map(e => e.event_type).filter(Boolean)))],
    [dateRangedEvents],
  );

  const filtered = useMemo(() => dateRangedEvents.filter(e => {
    if (filter === 'success' && !isSuccessCode(e.status_code)) return false;
    if (filter === 'error' && isSuccessCode(e.status_code)) return false;
    if (eventTypeFilter !== 'all' && e.event_type !== eventTypeFilter) return false;
    return true;
  }), [dateRangedEvents, filter, eventTypeFilter]);

  return (
    <>
      <div className="dashboard-wrapper">
        <div className="dashboard-page">
          <header className="dashboard-header">
            <div className="dashboard-header-main">
              <div className="dashboard-header-title-row">
                <div>
                  <h1 className="dashboard-title">Welcome, {firstName}</h1>
                  <p className="dashboard-subtitle">{companyName}</p>
                </div>
                <div className="dashboard-header-stats">
                  <div className="dashboard-header-stat dashboard-time-savings-stat">
                    <span className="dashboard-header-stat-label">
                      Est. Time Savings in {currentMonthLabel}
                    </span>
                    <span className="dashboard-header-stat-value">
                      {loading ? '…' : formatTimeSavings(monthTimeSavingsMinutes)}
                    </span>
                  </div>
                  <div className="dashboard-header-stat dashboard-invoice-stat">
                    <span className="dashboard-header-stat-label">
                      Invoiced in {currentMonthLabel}
                    </span>
                    <span className="dashboard-header-stat-value">
                      {loading ? '…' : formatCurrency(monthInvoiceTotal)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </header>

          <div className="dashboard-header-divider" role="separator" />

          <div className="stats-grid">
            <div className="stat-card">
              <span className="stat-value">{loading ? '…' : total}</span>
              <span className="stat-label">Total Events</span>
            </div>
            <div className="stat-card stat-card--success">
              <span className="stat-value">{loading ? '…' : successCount}</span>
              <span className="stat-label">Successful</span>
            </div>
            <div className="stat-card stat-card--error">
              <span className="stat-value">{loading ? '…' : errorCount}</span>
              <span className="stat-label">Failed</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">{loading ? '…' : `${successRate}%`}</span>
              <span className="stat-label">Success Rate</span>
            </div>
          </div>

          <div className="dashboard-filter-bar">
            {['all', 'success', 'error'].map(f => (
              <button
                key={f}
                type="button"
                className={`filter-btn${filter === f ? ' active' : ''}`}
                onClick={() => setFilter(f)}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
                {f === 'success' && <span className="filter-count">{successCount}</span>}
                {f === 'error' && <span className="filter-count filter-count--error">{errorCount}</span>}
              </button>
            ))}
            <div className="filter-divider" />
            <select
              className={`filter-select${eventTypeFilter !== 'all' ? ' active' : ''}`}
              value={eventTypeFilter}
              onChange={e => setEventTypeFilter(e.target.value)}
              aria-label="Filter by event type"
            >
              {eventTypes.map(type => (
                <option key={type} value={type}>
                  {type === 'all' ? 'All Event Types' : type}
                </option>
              ))}
            </select>
            <div className="filter-divider" />
            <select
              className={`filter-select${dateRangePreset !== '30' ? ' active' : ''}`}
              value={dateRangePreset}
              onChange={e => setDateRangePreset(e.target.value)}
              aria-label="Filter by date range"
            >
              {DATE_RANGE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className="event-list">
            <div className="event-list-header">
              <span>Event</span>
              <span>Customer / Route</span>
              <span>Document</span>
              <span>Status</span>
              <span>Date</span>
              <span></span>
            </div>

            {loading && (
              <div className="event-list-empty">Loading events…</div>
            )}

            {!loading && filtered.length === 0 && (
              <div className="event-list-empty">
                {events.length === 0
                  ? 'No sync events recorded yet.'
                  : dateRangedEvents.length === 0
                    ? 'No events in this date range.'
                    : 'No events match this filter.'}
              </div>
            )}

            {!loading && filtered.map(event => {
              const isSelected = selectedEvent?.id === event.id;
              const success = isSuccessCode(event.status_code);
              const customer = getEventCustomer(event);
              const route = getEventRouteLabel(event);
              const doc = getEventDocDisplay(event);

              return (
                <div key={event.id} className={`event-item${isSelected ? ' selected' : ''}`}>
                  <div className="event-row" onClick={() => setSelectedEvent(event)}>
                    <div className="event-col-id">
                      <span className="event-id-badge">#{event.id}</span>
                      <span className="event-type">{event.event_type}</span>
                    </div>

                    <div className="event-col-deal">
                      <span className="event-company">{customer}</span>
                      <span className="event-deal-name">{route}</span>
                    </div>

                    <div className="event-col-invoice">
                      {doc
                        ? <span className="event-invoice">{doc}</span>
                        : <span className="event-na">—</span>
                      }
                    </div>

                    <div className="event-col-status">
                      <span className={`status-badge ${success ? 'status-badge--success' : 'status-badge--error'}`}>
                        {success ? '✓' : '✕'} {event.status_code}
                      </span>
                    </div>

                    <div className="event-col-date">{formatEasternDate(event.create_date)}</div>

                    <div className="event-col-chevron">
                      <span className="event-chevron">›</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <EventDrawer event={selectedEvent} company={company} onClose={() => setSelectedEvent(null)} />
      <WelcomeModal />
    </>
  );
};
